<template>
  <component
    :is="state$$.layout"
    :api-version="modelRef.apiVersion"
    :class="{ 'app--offline': state$$?.isOffline }"
  >
    <template #header__left>
      <div class="app-pv__header__left">
        <Brand />
      </div>
    </template>

    <template #header__right>
      <!-- <div class="noprint"> -->
      <Toolbar v-if="!state$$?.isOffline" />
      <!-- </div> -->
    </template>

    <template #main__center__header>
      <div
        v-if="!state$$?.isOffline"
        class="noprint"
      >
        <Banner />
        <BreadCrumbs v-if="!state$$.inProgress" />
      </div>
    </template>

    <template #main__center>
      <OfflineStatus v-if="state$$?.isOffline" />
      <router-view />
    </template>

    <template #footer>
    </template>
  </component>
  <div
    v-if="state$$.isLoading"
    :class="{
      'app-in-loading': true,
    }"
  >
    <ProgressSpinner
      stroke-width="5"
      class="app-in-loading__spinner"
      :fill="primaryColor"
    />
  </div>
</template>

<script setup>
import {
  nextTick,
  onMounted,
  provide,
  reactive,
  computed,
  ref,
} from 'vue';

import {
  useImmer,
  useVuexModule,
  useAxios,
} from '@sttedra_ui/framework';

import AppViewModel from './app.view-model';
import AppViewState from './app.view-state';

import ProgressSpinner from 'primevue/progressspinner/sfc';

import Brand from '@/components/brand/Brand.vue';
import Banner from '@/components/banner/Banner.vue';
import BreadCrumbs from '@/components/bread-crumbs/BreadCrumbs.vue';
import Toolbar from '@/components/toolbar/Toolbar.vue';
import OfflineStatus from '@/components/offline-status/OfflineStatus.vue';

import {StatusCodes as HttpStatus} from 'http-status-codes';
import {useRouter} from 'vue-router';
import {useStore} from 'vuex';
import {useCssVar} from '@vueuse/core';
import useThemeChange from '@/composables/use-theme-change';
// import {getDualYearConfigDates} from '@/api/register/register.api';

// #region Params, Ref, Reactives, Computed
const {base} = useAxios();
const router = useRouter();
const store = useStore();

const {applyTheme, primaryColor} = useThemeChange();

const state = new AppViewState();
const state$$ = reactive(state);

const model = new AppViewModel();
const {ref: modelRef} = useImmer(model);
// #endregion

// #region Functions
base.interceptors.request.use(function(config) {
  if (store.getters['app/hasDualYear']) {
    config.headers['X-Campaign-Year'] = `${store.state.app.productYear ?? ''}`;
  };
  if (!state$$.token) {
    return config;
  }
  config.headers.Authorization = `Bearer ${state$$?.token ?? ''}`;
  return config;
}, function(error) {
  return Promise.reject(error);
});

base.interceptors.response.use(function(response) {
  switch (response.status) {
    case HttpStatus.FORBIDDEN: // 403 > Unauthorized
    case HttpStatus.UNAUTHORIZED: // 401 > Unauthenticated
    case HttpStatus.SERVICE_UNAVAILABLE: // 503 > Service Unavailable
    // case HttpStatus.BAD_REQUEST: // 400 // TODO: CHECK CONTROL
    // case HttpStatus.INTERNAL_SERVER_ERROR: // 500 // TODO: CHECK CONTROL
    case undefined: { // Network error (VPN)
      state$$.defaultStateApp();
      router.push({name: 'Home'});
      return;
    }
    default: { // Unknow
      break;
    }
  }
  return response;
}, function(error) {
  return Promise.reject(error);
});

router.beforeEach((to, from) => {
  if (to.name !== 'Home' && state$$.isOffline) {
    state$$.inProgress = false;
    state$$.isLoading = false;
    return {name: 'Home'};
  } else if (to.name !== 'Home' && to.name !== 'LoginOk' &&
    (to.name === 'AcceptedRequest' && !state$$.currentRequest) &&
    (to.name === 'DetailRequest' && !state$$.detailRequest) &&
    (to.name === 'EditRegisterContact' && !state$$.detailRequest)) {
    state$$.inProgress = false;
    state$$.isLoading = false;
    return {name: 'Home'};
  }
});

function setRoutes() {
  const storeModule = useVuexModule('mainnav');
  const routes = modelRef.value.mainNavRoutes;
  storeModule.commit('setRoutes', routes);
}

/**
 * Converts an image to a Base64 string.
 * @param {string} imagePath - The path to the image file.
 * @returns {Promise<string>} - A Base64 encoded string.
 */
async function convertToBase64(imagePath) {
  const response = await fetch(imagePath);
  const blob = await response.blob();

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

/**
 * Fetches text data from the store.
 * @returns {Promise<void>}
 */
async function fetchTexts() {
  await store.dispatch('text/fetchTexts');
}

/**
 * Sets up breadcrumbs dynamically based on the current route metadata.
 */
function setupBreadcrumbs() {
  state$$.breadCrumbs = computed(() => {
    const staticBreadCrumbs = router.currentRoute.value.meta?.staticBreadCrumbs || [];

    return staticBreadCrumbs.map((item) => {
      if (item.dynamic) {
        return {
          ...item,
          text: store.getters['text/getSectionTexts'](item.key),
        };
      }
      return item;
    });
  });
}

/**
 * Finalizes the loading process and applies styles to indicate the app is mounted.
 * @returns {Promise<void>}
 */
async function finalizeLoading() {
  await nextTick();
  document.body.classList.add('app-mounted');
}

/**
 * Sets up dynamic background images for the portal.
 * @returns {Promise<void>}
 */
async function setupDynamicBackgrounds() {
  const typePortal = ref(STTEDRA_TYPE_PORTAL);

  // Import images based on portal type
  // eslint-disable-next-line max-len
  const bannerDesktop = require(`@/assets/images/${typePortal.value}/banner-portal-background.${typePortal.value === 'abono-anual' ? 'jpg' : 'png'}`);
  const bannerMobile = require(`@/assets/images/${typePortal.value}/banner-portal-mobile.png`);
  const chevron = require(`@/assets/images/${typePortal.value}/chevron.svg`);

  // Convert images to Base64
  const base64Desktop = await convertToBase64(bannerDesktop);
  const base64Mobile = await convertToBase64(bannerMobile);
  const base64Chevron = await convertToBase64(chevron);

  // Update CSS variables dynamically
  applyCssVariables(base64Desktop, base64Mobile, base64Chevron);
}

/**
 * Applies the provided Base64 encoded images to CSS variables.
 * @param {string} base64Desktop - Base64 string for the desktop background image.
 * @param {string} base64Mobile - Base64 string for the mobile background image.
 * @param {string} base64Chevron - Base64 string for the chevron image.
 */
function applyCssVariables(base64Desktop, base64Mobile, base64Chevron) {
  const el = ref(null);

  const urlBanner = useCssVar('--url-banner', null);
  urlBanner.value = `url('${base64Desktop}')`;

  const urlBannerMobile = useCssVar('--url-banner-phone', el);
  urlBannerMobile.value = `url('${base64Mobile}')`;

  const urlChevron = useCssVar('--url-chevron', el);
  urlChevron.value = `url(${base64Chevron})`;

  applyTheme();
}

// async function setDualYearConfig() {
//   if (!store.state.app.dualYear) return;
//   try {
//     const {data} = await getDualYearConfigDates();
//     const {startDate, endDate} = data;
//     store.dispatch('app/setDualPeriodStart', startDate);
//     store.dispatch('app/setDualPeriodEnd', endDate);
//   } catch (error) {
//     store.dispatch('app/setDualPeriodStart', STTEDRA_DUAL_PERIOD_START);
//     store.dispatch('app/setDualPeriodEnd', STTEDRA_DUAL_PERIOD_END);
//   }
// }
/**
 * Initializes the application by setting configurations, dynamic styles, and loading necessary data.
 */
async function init() {
  // #region init config
  await fetchTexts();
  setupBreadcrumbs();
  await setupDynamicBackgrounds();
  setRoutes();
  // #endregion

  // #region Loading
  await finalizeLoading();

  // await setDualYearConfig();
  // #endregion
}
// #endregion

// #region Effects, Hooks
onMounted(init);
// #endregion

// #region Provide, Expose
provide('appState$$', state$$);
// #endregion
</script>

<style lang="scss" scoped>
// Scoped styles</style>

<style lang="scss">
@use '@/styles/vars' as *;
@use '@/styles/breakpoints' as *;

.app__header,
article {
  @include media("<=phone") {
    padding: $brand__boxspace--medium (
      $brand__boxspace--medium * 1) $brand__boxspace--medium (
      $brand__boxspace--medium * 1
    );
}

@include media(">phone") {
  padding: $brand__boxspace--medium (
    $brand__boxspace--medium * 2) $brand__boxspace--medium (
    $brand__boxspace--medium * 2
  );
}

@include media(">phone", "<=tablet") {
  padding: $brand__boxspace--medium (
    $brand__boxspace--medium * 3) $brand__boxspace--medium (
    $brand__boxspace--medium * 3
  );
}

@include media(">tablet", "<=1224px") {
  padding: $brand__boxspace--medium (
    $brand__boxspace--medium * 4) $brand__boxspace--medium (
    $brand__boxspace--medium * 4
  );
}

@include media(">1224px") {
  padding: $brand__boxspace--medium (
    $brand__boxspace--medium * 20) $brand__boxspace--medium (
    $brand__boxspace--medium * 20
  );
}
}

// BUTTONS
.p-button {
  .p-button-label {
    @include media("<=phone") {
      font-size: $pv__fs--large;
    }

    @include media(">phone") {
      font-size: $pv__fs--large;
    }
  }
}

.p-inputtext {
  @include media("<=phone") {
    font-size: 1.2rem !important;
  }
}

.p-dropdown-label {
  @include media("<=phone") {
    margin-top: -5px !important;
  }
}

.toasts {
  @include media("<=tablet") {
    width: 100%;
  }
}
</style>
